import React, { useEffect, useRef, useState } from 'react';
import {
  AlertCircle,
  CheckCircle,
  Download,
  ExternalLink,
  FileText,
  Loader2,
  UploadCloud,
  X,
} from 'lucide-react';

import { instrumentApi } from '../services/api';
import { REVIEWER_DEFAULT_MODEL } from '../constants/models';
import {
  InstrumentReview,
  InstrumentReviewStatus,
} from '../types/instrument';

type Phase = 'idle' | 'uploading' | 'reviewing' | 'done' | 'error';

// Single source of truth for what the upload accepts. The HTML `accept`
// attribute and the post-drop validation regex are both derived from it
// so they can't drift.
const ACCEPTED_EXTENSIONS = ['.qsf', '.json'] as const;
const ACCEPT_ATTR = ACCEPTED_EXTENSIONS.join(',');
const ACCEPTED_FILE_PATTERN = new RegExp(
  `\\.(${ACCEPTED_EXTENSIONS.map((e) => e.slice(1)).join('|')})$`,
  'i',
);

// Reviews typically take 30–90s. Poll fast at the start (so a quick run feels
// snappy) then back off to avoid burning ~45 wasted requests on a slow run.
const POLL_FAST_MS = 2000;
const POLL_SLOW_MS = 5000;
const POLL_FAST_TICKS = 5;

const InstrumentTab: React.FC = () => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [file, setFile] = useState<File | null>(null);
  const [reviewId, setReviewId] = useState<string>('');
  const [status, setStatus] = useState<InstrumentReviewStatus | null>(null);
  const [result, setResult] = useState<InstrumentReview | null>(null);
  const [error, setError] = useState<string>('');

  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pollTickCount = useRef(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const isWorking = phase === 'uploading' || phase === 'reviewing';

  useEffect(() => {
    return () => {
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const stopPolling = () => {
    if (pollTimer.current) {
      clearTimeout(pollTimer.current);
      pollTimer.current = null;
    }
  };

  const startPolling = (id: string) => {
    stopPolling();
    pollTickCount.current = 0;

    const tick = async () => {
      pollTickCount.current += 1;
      const res = await instrumentApi.getStatus(id);
      if (res.error) {
        setError(res.error);
        setPhase('error');
        stopPolling();
        return;
      }
      if (res.data) {
        const next = res.data;
        setStatus((prev) =>
          prev && prev.status === next.status && prev.message === next.message
            ? prev
            : next,
        );
        if (next.status === 'completed') {
          stopPolling();
          const r = await instrumentApi.getResults(id);
          if (r.data) {
            setResult(r.data);
            setPhase('done');
          } else if (r.error) {
            setError(r.error);
            setPhase('error');
          }
          return;
        }
        if (next.status === 'failed') {
          stopPolling();
          const r = await instrumentApi.getResults(id);
          setError(r.error || 'Review failed.');
          setPhase('error');
          return;
        }
      }
      const delay = pollTickCount.current < POLL_FAST_TICKS ? POLL_FAST_MS : POLL_SLOW_MS;
      pollTimer.current = setTimeout(tick, delay);
    };

    pollTimer.current = setTimeout(tick, POLL_FAST_MS);
  };

  const acceptFile = (f: File | null) => {
    if (!f) return;
    if (!ACCEPTED_FILE_PATTERN.test(f.name)) {
      setError(`"${f.name}" doesn't look like a Qualtrics .qsf file.`);
      return;
    }
    setFile(f);
    setError('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    acceptFile(e.target.files?.[0] || null);
    // Reset value (even on rejected files) so picking the same file twice
    // still fires onChange.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    if (!isWorking) setIsDragging(true);
  };
  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    // dragleave fires when the cursor crosses a child element's boundary
    // (icon, paragraph, etc.). Ignore those — only clear when leaving the
    // dropzone itself.
    const next = e.relatedTarget as Node | null;
    if (next && e.currentTarget.contains(next)) return;
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    if (isWorking) return;
    acceptFile(e.dataTransfer.files?.[0] || null);
  };

  const triggerBrowse = () => {
    if (isWorking) return;
    fileInputRef.current?.click();
  };

  const clearFile = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    setFile(null);
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setError('Please choose a .qsf file to review.');
      return;
    }
    setPhase('uploading');
    setError('');
    setResult(null);
    setStatus(null);

    const res = await instrumentApi.submitReview(file, REVIEWER_DEFAULT_MODEL);
    if (res.error || !res.data) {
      setError(res.error || 'Upload failed.');
      setPhase('error');
      return;
    }
    setReviewId(res.data.review_id);
    setPhase('reviewing');
    startPolling(res.data.review_id);
  };

  const handleStartOver = () => {
    stopPolling();
    setPhase('idle');
    setFile(null);
    setReviewId('');
    setStatus(null);
    setResult(null);
    setError('');
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <div className="flex items-center mb-4 space-x-3">
          <FileText className="w-6 h-6 text-primary-600" />
          <h2 className="text-xl font-semibold">Instrument Review</h2>
        </div>
        <p className="text-sm text-gray-600 mb-6">
          Upload a Qualtrics <code className="px-1 rounded bg-gray-100">.qsf</code> export. Survey Shield runs a review of the instrument
          and returns a defense score, completion-likelihood, recommendations.
        </p>

        {phase !== 'done' && (
          <form onSubmit={handleSubmit} className="space-y-5">
            <input
              ref={fileInputRef}
              type="file"
              accept={ACCEPT_ATTR}
              onChange={handleFileChange}
              className="hidden"
            />

            {!file ? (
              <div
                role="button"
                tabIndex={0}
                aria-label="Drop a QSF file or click to browse"
                onClick={triggerBrowse}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    triggerBrowse();
                  }
                }}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={[
                  'relative flex flex-col items-center justify-center',
                  'border-2 border-dashed rounded-xl p-12 text-center',
                  'transition-colors cursor-pointer outline-none',
                  isDragging
                    ? 'border-primary-500 bg-primary-50'
                    : 'border-gray-300 bg-gray-50 hover:border-primary-400 hover:bg-primary-50/50',
                  isWorking ? 'opacity-50 pointer-events-none' : '',
                ].join(' ')}
              >
                <UploadCloud className={`w-12 h-12 mb-3 ${isDragging ? 'text-primary-600' : 'text-gray-400'}`} />
                <p className="text-base font-medium text-gray-900 mb-1">
                  Drop your <code className="px-1.5 py-0.5 rounded bg-white border text-sm">.qsf</code> file here
                </p>
                <p className="text-sm text-gray-500 mb-3">or click to browse</p>
                <p className="text-xs text-gray-400">
                  Qualtrics survey export · stays on your machine until you submit
                </p>
              </div>
            ) : (
              <div className="flex items-center justify-between p-4 bg-white border-2 border-primary-200 rounded-xl">
                <div className="flex items-center space-x-3 min-w-0">
                  <FileText className="w-8 h-8 text-primary-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{file.name}</p>
                    <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} KB</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={clearFile}
                  disabled={isWorking}
                  aria-label="Remove file"
                  className="flex-shrink-0 p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={isWorking || !file}
              className="w-full px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
            >
              {isWorking ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{phase === 'uploading' ? 'Uploading…' : 'Reviewing instrument…'}</span>
                </>
              ) : (
                <span>Start Review</span>
              )}
            </button>
          </form>
        )}

        {phase === 'reviewing' && status && (
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start space-x-3">
            <Loader2 className="w-5 h-5 text-blue-600 animate-spin mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm font-medium text-blue-900">{status.message}</p>
              <p className="text-xs text-blue-700 mt-1">Review ID: <code>{reviewId}</code></p>
              <p className="text-xs text-blue-700 mt-1">
                Typically 30–90 seconds for a small instrument. Each category is reviewed in parallel.
              </p>
            </div>
          </div>
        )}

        {phase === 'error' && (
          <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start space-x-3">
            <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium text-red-900">Review did not complete</p>
              <p className="text-xs text-red-700 mt-1">{error}</p>
              <button
                type="button"
                onClick={handleStartOver}
                className="mt-3 text-sm text-red-700 underline hover:text-red-900"
              >
                Start over
              </button>
            </div>
          </div>
        )}

        {phase === 'done' && result && (
          <div className="space-y-4">
            <div className="p-4 bg-green-50 border border-green-200 rounded-lg flex items-start space-x-3">
              <CheckCircle className="w-5 h-5 text-green-700 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-green-900">
                  Review complete · {result.categories.length} categor{result.categories.length !== 1 ? 'ies' : 'y'}
                </p>
                <p className="text-xs text-green-800 mt-1">{result.filename}</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <ScoreTile label="Defense score" value={result.overall_score} />
              <ScoreTile label="Bot completion likelihood" value={result.completion_likelihood} invertColor />
              <ScoreTile label="Categories" value={result.categories.length} suffix="" raw />
            </div>

            {result.overall_feedback?.headline && (
              <div className="bg-white border rounded-lg p-4">
                <p className="text-sm font-medium text-gray-900 mb-1">Bot-resistance verdict</p>
                <p className="text-sm text-gray-700">{result.overall_feedback.headline}</p>
              </div>
            )}

            <div className="flex space-x-3">
              <a
                href={instrumentApi.reportUrl(result.review_id)}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 px-4 py-3 bg-primary-600 text-white rounded-lg font-medium hover:bg-primary-700 transition-colors flex items-center justify-center space-x-2"
              >
                <ExternalLink className="w-4 h-4" />
                <span>Open Full Report</span>
              </a>
              <a
                href={`${instrumentApi.reportUrl(result.review_id)}?download=1`}
                className="flex-1 px-4 py-3 border border-primary-600 text-primary-700 rounded-lg font-medium hover:bg-primary-50 transition-colors flex items-center justify-center space-x-2"
              >
                <Download className="w-4 h-4" />
                <span>Download Report</span>
              </a>
              <button
                type="button"
                onClick={handleStartOver}
                className="px-4 py-3 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
              >
                Review another
              </button>
            </div>

            <div className="bg-gray-50 border rounded-lg p-4">
              <p className="text-sm font-medium text-gray-900 mb-2">Methods statement</p>
              <p className="text-xs text-gray-600 mb-2">
                Paste this into your Methods section. The full report has dedicated copy buttons for the methods statement and the citation.
              </p>
              <textarea
                readOnly
                value={result.methods_statement}
                className="w-full text-xs font-mono p-3 bg-white border border-gray-300 rounded resize-y"
                rows={4}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

interface ScoreTileProps {
  label: string;
  value: number;
  suffix?: string;
  raw?: boolean;
  invertColor?: boolean; // for "higher is worse" metrics (bot completion)
}

const ScoreTile: React.FC<ScoreTileProps> = ({ label, value, suffix = '/100', raw, invertColor }) => {
  let color = 'text-gray-900';
  if (!raw) {
    const high = value >= 70;
    const mid = value >= 40;
    if (invertColor) {
      color = high ? 'text-red-600' : mid ? 'text-yellow-600' : 'text-green-600';
    } else {
      color = high ? 'text-green-600' : mid ? 'text-yellow-600' : 'text-red-600';
    }
  }
  return (
    <div className="bg-white border rounded-lg p-3 text-center">
      <div className={`text-3xl font-bold ${color}`}>
        {Math.round(value)}
        {!raw && <span className="text-base font-medium text-gray-500">{suffix}</span>}
      </div>
      <div className="text-xs text-gray-500 mt-1">{label}</div>
      {invertColor && !raw && (
        <div className="text-[10px] text-gray-400 mt-0.5">lower is better</div>
      )}
    </div>
  );
};

export default InstrumentTab;
